import React, { Component } from 'react';
import { Rnd } from 'react-rnd';
import './RichTextCanvas.css';

class RichTextCanvas extends Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.editableRefs = [];
        // Track which layer is in text-editing mode. This is separate from
        // selection: a selected layer can be dragged/rotated, while an editing
        // layer is contentEditable (and dragging is disabled so the caret works).
        this.state = { editingLayerIndex: null };
    }

    componentDidMount() {
        this.updateEditableContent();
    }

    componentDidUpdate(prevProps) {
        // Only update content if the layer text changed externally (not from user editing)
        const { textLayers, selectedLayerIndex } = this.props;
        const { editingLayerIndex } = this.state;

        textLayers.forEach((layer, index) => {
            const prevLayer = prevProps.textLayers[index];
            const isEditing = editingLayerIndex === index;

            // Only update if text changed AND we're not currently editing this layer
            if (prevLayer && layer.text !== prevLayer.text && !isEditing) {
                const ref = this.editableRefs[index];
                if (ref && ref.innerHTML !== layer.text) {
                    // Convert \n to <br> for display in contentEditable
                    const htmlText = layer.text.replace(/\n/g, '<br>');
                    ref.innerHTML = htmlText;
                }
            }
        });

        // If selection moved to a different layer (or was cleared), leave edit mode.
        if (selectedLayerIndex !== prevProps.selectedLayerIndex &&
            editingLayerIndex !== null &&
            editingLayerIndex !== selectedLayerIndex) {
            this.setState({ editingLayerIndex: null });
        }

        // Set content for newly added layers, and drop straight into edit mode
        // on the new layer so the user can type immediately.
        if (textLayers.length > prevProps.textLayers.length) {
            this.updateEditableContent();
            this.startEditing(textLayers.length - 1);
        }
    }

    // Enter text-editing mode for a layer and focus its contentEditable element.
    startEditing = (index) => {
        if (this.state.editingLayerIndex === index) return;
        this.props.onSelectLayer(index);
        this.setState({ editingLayerIndex: index }, () => {
            const ref = this.editableRefs[index];
            if (ref) ref.focus();
        });
    };

    stopEditing = () => {
        if (this.state.editingLayerIndex !== null) {
            this.setState({ editingLayerIndex: null });
        }
    };

    updateEditableContent = () => {
        const { textLayers } = this.props;
        textLayers.forEach((layer, index) => {
            const ref = this.editableRefs[index];
            if (ref && ref.innerHTML !== layer.text) {
                // Convert \n to <br> for display in contentEditable
                const htmlText = layer.text.replace(/\n/g, '<br>');
                ref.innerHTML = htmlText;
            }
        });
    }

    handleDragStop = (index, e, data) => {
        const { zoom } = this.props;
        this.props.onDragStop(index, {
            x: data.x / zoom,
            y: data.y / zoom,
        });

        // If this box was being edited and moved via the handle, put the caret /
        // selection back where it was so the user can keep editing. The position
        // update above re-renders, so restore on the next frame.
        if (this.state.editingLayerIndex === index && this.savedDragRange) {
            const range = this.savedDragRange;
            this.savedDragRange = null;
            const ref = this.editableRefs[index];
            requestAnimationFrame(() => {
                if (ref) ref.focus();
                this.restoreSavedSelection(range);
            });
        }
    };

    handleResize = (index, e, direction, ref, delta, position) => {
        const { zoom } = this.props;
        this.props.onUpdateLayer(index, {
            x: position.x / zoom,
            y: position.y / zoom,
        });
    };

    handleContentChange = (index, e) => {
        const content = e.currentTarget.innerHTML;
        this.props.onUpdateLayer(index, { text: content });
    };

    handleFocus = (index) => {
        this.props.onSelectLayer(index);
    };

    handleRotationStart = (index, e) => {
        e.stopPropagation();
        this.rotatingIndex = index;
        this.rotationStartAngle = this.props.textLayers[index].rotation || 0;

        const element = e.currentTarget.parentElement;
        const rect = element.getBoundingClientRect();
        this.rotationCenter = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        };

        // Calculate initial mouse angle
        const dx = e.clientX - this.rotationCenter.x;
        const dy = e.clientY - this.rotationCenter.y;
        this.initialMouseAngle = Math.atan2(dy, dx) * (180 / Math.PI);

        document.addEventListener('mousemove', this.handleRotationMove);
        document.addEventListener('mouseup', this.handleRotationEnd);
    };

    handleRotationMove = (e) => {
        if (this.rotatingIndex === null) return;

        const dx = e.clientX - this.rotationCenter.x;
        const dy = e.clientY - this.rotationCenter.y;
        const currentMouseAngle = Math.atan2(dy, dx) * (180 / Math.PI);

        // Calculate the difference from initial mouse angle and add to starting rotation
        const angleDiff = currentMouseAngle - this.initialMouseAngle;
        const newRotation = this.rotationStartAngle + angleDiff;

        this.props.onUpdateLayer(this.rotatingIndex, { rotation: Math.round(newRotation) });
    };

    handleRotationEnd = () => {
        this.rotatingIndex = null;
        document.removeEventListener('mousemove', this.handleRotationMove);
        document.removeEventListener('mouseup', this.handleRotationEnd);
    };

    isEditingText = (index) => {
        return this.state.editingLayerIndex === index;
    };

    handleKeyDown = (index, e) => {
        // Escape leaves edit mode (blur triggers stopEditing). The layer stays
        // selected, so it can be dragged straight away.
        if (e.key === 'Escape') {
            e.preventDefault();
            const ref = this.editableRefs[index];
            if (ref) ref.blur();
            return;
        }
        // Cmd/Ctrl+B for bold
        if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
            e.preventDefault();
            document.execCommand('bold', false, null);
            this.handleContentChange(index, e);
        }
        // Cmd/Ctrl+I for italic
        else if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
            e.preventDefault();
            document.execCommand('italic', false, null);
            this.handleContentChange(index, e);
        }
        // Cmd/Ctrl+U for underline
        else if ((e.metaKey || e.ctrlKey) && e.key === 'u') {
            e.preventDefault();
            document.execCommand('underline', false, null);
            this.handleContentChange(index, e);
        }
    };

    handlePaste = (index, e) => {
        e.preventDefault();

        // Get plain text from clipboard
        const text = e.clipboardData.getData('text/plain');

        // Insert the plain text at cursor position
        document.execCommand('insertText', false, text);

        // Trigger content update
        this.handleContentChange(index, e);
    };

    renderTextLayer = (layer, index) => {
        const { selectedLayerIndex, zoom, onSelectLayer } = this.props;
        const isSelected = selectedLayerIndex === index;
        const isEditing = this.isEditingText(index);

        // Container style for Rnd - let Rnd handle positioning
        const containerStyle = {
            margin: 0,
            padding: 0,
        };

        // Base text style - scale everything with zoom
        // Calculate baseline offset: baseline_y = y + (fontSize × 0.8)
        // This is where the text baseline sits, and where rotation should pivot
        const baselineOffsetY = layer.fontSize * 0.8 * zoom;

        const baseStyle = {
            fontSize: `${layer.fontSize * zoom}px`,
            fontFamily: layer.fontFamily,
            textAlign: layer.textAlign,
            opacity: layer.opacity,
            cursor: isEditing ? 'text' : 'move',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            padding: 0,
            margin: 0,
            border: 'none',
            background: isSelected ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
            outline: isSelected ? `${2 * zoom}px solid #007bff` : 'none',
            outlineOffset: isSelected ? `${2 * zoom}px` : '0',
            minWidth: `${50 * zoom}px`,
            minHeight: `${30 * zoom}px`,
            lineHeight: 'normal',
            verticalAlign: 'top',
        };

        // Set base color (can be overridden by inline HTML)
        baseStyle.color = layer.textColour;

        // Add stroke/outline effect - create a circular outline (scaled with zoom)
        if (layer.strokeColour && layer.strokeWidth > 0) {
            const shadows = [];
            const scaledStrokeWidth = layer.strokeWidth * zoom;
            for (let angle = 0; angle < 360; angle += 45) {
                const radian = (angle * Math.PI) / 180;
                const x = Math.cos(radian) * scaledStrokeWidth;
                const y = Math.sin(radian) * scaledStrokeWidth;
                shadows.push(`${x}px ${y}px 0 ${layer.strokeColour}`);
            }
            baseStyle.textShadow = shadows.join(', ');
        }

        // Backend positioning formula: baseline_y = y + (fontSize × 0.8)
        // This means 'y' represents the top edge of the text bounding box,
        // and the baseline is at y + (fontSize × 0.8)
        // Our HTML div also positions from the top edge, so we just use y directly

        // Rotate the whole box (text + handles) as one unit so the handles stay
        // attached to the rotated text instead of floating in the unrotated spot.
        const rotationWrapperStyle = {
            position: 'relative',
            display: 'inline-block',
            transform: `rotate(${layer.rotation}deg)`,
            transformOrigin: `0 ${baselineOffsetY}px`, // Rotate around baseline position
        };

        return (
            <Rnd
                key={index}
                default={{
                    x: layer.x * zoom,
                    y: layer.y * zoom,
                    width: 'auto',
                    height: 'auto',
                }}
                position={{ x: layer.x * zoom, y: layer.y * zoom }}
                onDragStop={(e, data) => this.handleDragStop(index, e, data)}
                onResizeStop={(e, direction, ref, delta, position) => this.handleResize(index, e, direction, ref, delta, position)}
                bounds="parent"
                minWidth={50}
                minHeight={30}
                enableResizing={false}
                // Never fully disable dragging. While editing, restrict the drag
                // to the dedicated move handle so mouse clicks/selection inside the
                // text still work; when not editing, the whole box is draggable.
                disableDragging={false}
                dragHandleClassName={isEditing ? 'rtc-move-handle' : undefined}
                // Don't let react-draggable apply `user-select: none` during a drag –
                // it collapses the caret/selection inside the contentEditable text.
                enableUserSelectHack={false}
                style={containerStyle}
            >
                <div style={rotationWrapperStyle}>
                <div
                    ref={el => (this.editableRefs[index] = el)}
                    className="text-content"
                    contentEditable={isEditing}
                    suppressContentEditableWarning={true}
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering canvas deselect
                        // Single click only selects (so the layer can be dragged);
                        // double click enters text-editing mode.
                        if (!isEditing) onSelectLayer(index);
                    }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        this.startEditing(index);
                    }}
                    onFocus={() => this.handleFocus(index)}
                    onBlur={(e) => {
                        this.handleContentChange(index, e);
                        this.stopEditing();
                    }}
                    onKeyDown={(e) => this.handleKeyDown(index, e)}
                    onPaste={(e) => this.handlePaste(index, e)}
                    style={baseStyle}
                />
                {isSelected && (
                    /* Move handle — draggable even while editing text. */
                    <div
                        className="rtc-move-handle"
                        // Keep the text's focus/selection when grabbing the handle:
                        // preventing the mousedown default stops the contentEditable
                        // from blurring (which would collapse the selection and exit
                        // edit mode). We also stash the current selection so it can be
                        // restored after the drag. The drag still starts normally.
                        onMouseDown={(e) => {
                            e.preventDefault();
                            this.savedDragRange = this.saveCurrentSelection();
                        }}
                        style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '-10px',
                            width: '20px',
                            height: '20px',
                            background: '#28a745',
                            borderRadius: '50%',
                            cursor: 'move',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        title="Drag to move"
                    >
                        <i className="fas fa-arrows-alt" style={{ fontSize: '11px', color: 'white' }} />
                    </div>
                )}
                {isSelected && !isEditing && (
                    <>
                        {/* Rotation handle */}
                        <div
                            onMouseDown={(e) => this.handleRotationStart(index, e)}
                            style={{
                                position: 'absolute',
                                top: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '20px',
                                height: '20px',
                                background: '#007bff',
                                borderRadius: '50%',
                                cursor: 'grab',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                zIndex: 1000,
                            }}
                            title="Drag to rotate"
                        >
                            <i className="fas fa-sync-alt" style={{ fontSize: '10px', color: 'white', position: 'absolute', top: '3px', left: '3px' }} />
                        </div>
                        {/* Rotation angle display */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                this.props.onUpdateLayer(index, { rotation: 0 });
                            }}
                            style={{
                                position: 'absolute',
                                top: '-55px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '3px',
                                fontSize: '11px',
                                whiteSpace: 'nowrap',
                                cursor: layer.rotation !== 0 ? 'pointer' : 'default',
                                zIndex: 999,
                            }}
                            title={layer.rotation !== 0 ? 'Click to reset to 0°' : 'Current rotation'}
                        >
                            {layer.rotation}°
                        </div>
                        {/* Delete button */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                this.props.onRemoveLayer(index);
                            }}
                            style={{
                                position: 'absolute',
                                top: '-10px',
                                right: '-10px',
                                width: '20px',
                                height: '20px',
                                background: '#dc3545',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            title="Delete layer"
                        >
                            <i className="fas fa-times" style={{ fontSize: '12px', color: 'white' }} />
                        </div>
                    </>
                )}
                </div>
            </Rnd>
        );
    };

    getSelectedTextInfo = () => {
        const { selectedLayerIndex } = this.props;
        if (selectedLayerIndex === null) return null;

        const ref = this.editableRefs[selectedLayerIndex];
        if (!ref) return null;

        const selection = window.getSelection();
        if (!selection.rangeCount) return null;

        const range = selection.getRangeAt(0);

        // Check if selection is within our editable element
        if (!ref.contains(range.commonAncestorContainer)) {
            return null;
        }

        return {
            selection,
            range,
            hasSelection: !range.collapsed,
        };
    };

    saveCurrentSelection = () => {
        const { selectedLayerIndex } = this.props;
        if (selectedLayerIndex === null) {
            return null;
        }

        const ref = this.editableRefs[selectedLayerIndex];
        if (!ref) {
            return null;
        }

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            // Make sure the selection is within our element
            if (ref.contains(range.commonAncestorContainer)) {
                return range.cloneRange();
            }
        }
        return null;
    };

    restoreSavedSelection = (savedRange) => {
        if (!savedRange) {
            return false;
        }

        try {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedRange);
            return true;
        } catch (e) {
            console.error('restoreSavedSelection: error', e);
            return false;
        }
    };

    applyFormatting = (command, value = null, savedRange = null) => {
        const { selectedLayerIndex } = this.props;
        if (selectedLayerIndex === null) return;

        const ref = this.editableRefs[selectedLayerIndex];
        if (!ref) return;

        // Make sure the element has focus
        ref.focus();

        // If we have a saved range, restore it
        let hasSelection = false;
        if (savedRange) {
            hasSelection = this.restoreSavedSelection(savedRange);
        } else {
            // Check if there's a current selection
            const selection = window.getSelection();
            hasSelection = selection && selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed;
        }

        if (hasSelection) {
            // Apply formatting to selected text only
            document.execCommand(command, false, value);
        } else {
            // If no text is selected and this is a color command, select all text first
            if (command === 'foreColor') {
                const range = document.createRange();
                range.selectNodeContents(ref);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand(command, false, value);
                // Collapse selection to end
                selection.collapseToEnd();
            } else {
                // For other commands, just apply normally
                document.execCommand(command, false, value);
            }
        }

        // Trigger content update
        const content = ref.innerHTML;
        this.props.onUpdateLayer(selectedLayerIndex, { text: content });
    };

    render() {
        const { textLayers, backgroundImageUrl, canvasWidth, canvasHeight, zoom } = this.props;

        const canvasStyle = {
            width: `${canvasWidth * zoom}px`,
            height: `${canvasHeight * zoom}px`,
            backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top left',
            position: 'relative',
            margin: '20px auto',
            padding: 0,
            border: '1px solid #ddd',
            backgroundColor: '#f5f5f5',
            boxSizing: 'border-box',
        };

        return (
            <div
                id="rich-text-canvas-wrapper"
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    backgroundColor: '#e0e0e0',
                    padding: '20px',
                    boxSizing: 'border-box',
                }}
            >
                <div
                    ref={this.canvasRef}
                    style={canvasStyle}
                    onMouseDown={(e) => {
                        // If pressing directly on the canvas (not a text layer), deselect.
                        // Do this on mousedown — before the text's blur fires — so we go
                        // straight from editing to deselected, without a one-frame render
                        // of the selected-but-not-editing state (which flashes the
                        // rotate/delete handles).
                        if (e.target === this.canvasRef.current) {
                            this.props.onSelectLayer(null);
                        }
                    }}
                >
                    {textLayers.map((layer, index) => this.renderTextLayer(layer, index))}
                </div>
            </div>
        );
    }
}

export default RichTextCanvas;
